// frontend-react/src/components/RuleManagement/DepartmentAssignModal.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Space, message, Tabs, Select, Button, List, Avatar, Tag } from 'antd';
import { TeamOutlined, UserOutlined, DeleteOutlined } from '@ant-design/icons';
import { RuleSet, Department } from '../../types/rule';
import {
  loadDepartments,
  loadRulePermissions,
  updateRulePermissions,
  loadCompanyMembers,
  loadRuleUserPermissions,
  grantUserRulePermission,
  revokeUserRulePermission,
} from '../../services/ruleService';

interface DepartmentAssignModalProps {
  visible: boolean;
  ruleSet: RuleSet | null;
  onClose: () => void;
}

interface UserPermission {
  permission_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
}

interface Member {
  id: number;
  name: string;
  email: string;
}

const DepartmentAssignModal: React.FC<DepartmentAssignModalProps> = ({
  visible,
  ruleSet,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState('department');

  // 部門 Tab 狀態
  const [departments, setDepartments] = useState<Department[]>([]);
  const [assignedDepartmentIds, setAssignedDepartmentIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // 個人指派 Tab 狀態
  const [members, setMembers] = useState<Member[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isGranting, setIsGranting] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  useEffect(() => {
    if (visible && ruleSet) {
      loadDeptData();
      loadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, ruleSet]);

  const loadDeptData = async () => {
    if (!ruleSet) return;
    setIsLoading(true);
    try {
      const [depts, permissions] = await Promise.all([
        loadDepartments(),
        loadRulePermissions(ruleSet.id),
      ]);
      setDepartments(depts);
      setAssignedDepartmentIds(permissions);
    } catch (error) {
      console.error('載入部門資料失敗:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserData = async () => {
    if (!ruleSet) return;
    setIsLoadingUsers(true);
    try {
      const [allMembers, userPerms] = await Promise.all([
        loadCompanyMembers(),
        loadRuleUserPermissions(ruleSet.id),
      ]);
      setMembers(allMembers);
      setUserPermissions(userPerms);
    } catch (error) {
      console.error('載入成員資料失敗:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleSaveDepts = async () => {
    if (!ruleSet) return;
    setIsSaving(true);
    const result = await updateRulePermissions(ruleSet.id, assignedDepartmentIds);
    if (result.success) {
      message.success('部門指派已更新');
      onClose();
    } else {
      message.error(`更新失敗: ${result.error}`);
    }
    setIsSaving(false);
  };

  const handleGrantUser = async () => {
    if (!ruleSet || !selectedUserId) return;
    setIsGranting(true);
    const result = await grantUserRulePermission(ruleSet.id, selectedUserId);
    if (result.success) {
      message.success('已授予個人權限');
      setSelectedUserId(null);
      await loadUserData();
    } else {
      message.error(result.error || '授予失敗');
    }
    setIsGranting(false);
  };

  const handleRevokeUser = async (userId: number, userName: string) => {
    if (!ruleSet) return;
    const result = await revokeUserRulePermission(ruleSet.id, userId);
    if (result.success) {
      message.success(`已撤銷 ${userName} 的個人授權`);
      setUserPermissions(prev => prev.filter(p => p.user_id !== userId));
    } else {
      message.error(result.error || '撤銷失敗');
    }
  };

  // 過濾掉已有個人授權的成員
  const assignedUserIds = new Set(userPermissions.map(p => p.user_id));
  const availableMembers = members.filter(m => !assignedUserIds.has(m.id));

  const ruleInfo = ruleSet && (
    <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f5f5f5', borderRadius: 4 }}>
      <strong>規則集：</strong>
      {ruleSet.rule_set_name}
      <span style={{ color: '#999', marginLeft: 8 }}>({ruleSet.rules_count} 條規則)</span>
    </div>
  );

  const deptTab = (
    <div>
      <div style={{ marginBottom: 8, fontWeight: 500 }}>選擇要指派的部門：</div>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 20 }}>載入中...</div>
      ) : departments.length === 0 ? (
        <div style={{ color: '#999', padding: 12, background: '#fafafa', borderRadius: 4 }}>
          目前沒有可用的部門
        </div>
      ) : (
        <div style={{ maxHeight: 280, overflow: 'auto' }}>
          <Checkbox.Group
            value={assignedDepartmentIds}
            onChange={values => setAssignedDepartmentIds(values as number[])}
            style={{ width: '100%' }}
          >
            <Space direction='vertical' style={{ width: '100%' }}>
              {departments.map(dept => (
                <Checkbox key={dept.id} value={dept.id} style={{ width: '100%' }}>
                  <span style={{ marginLeft: 8 }}>{dept.name}</span>
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </div>
      )}
      <div
        style={{ marginTop: 12, padding: 12, background: '#e6f4f8', borderRadius: 4, fontSize: 13 }}
      >
        指派後，該部門的所有成員都可以使用此規則集進行審閱。
      </div>
    </div>
  );

  const userTab = (
    <div>
      <div style={{ marginBottom: 8, fontWeight: 500 }}>指派給個人成員：</div>
      <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
        <Select
          style={{ flex: 1 }}
          placeholder='搜尋或選擇成員'
          showSearch
          value={selectedUserId}
          onChange={setSelectedUserId}
          loading={isLoadingUsers}
          filterOption={(input, option) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
          }
          options={availableMembers.map(m => ({
            value: m.id,
            label: `${m.name}（${m.email}）`,
          }))}
          notFoundContent={availableMembers.length === 0 ? '無其他成員可指派' : undefined}
        />
        <Button
          type='primary'
          onClick={handleGrantUser}
          disabled={!selectedUserId}
          loading={isGranting}
        >
          新增
        </Button>
      </Space.Compact>

      <div style={{ marginBottom: 8, fontWeight: 500 }}>
        已個人授權的成員
        {userPermissions.length > 0 && (
          <Tag style={{ marginLeft: 8 }}>{userPermissions.length}</Tag>
        )}
      </div>
      {userPermissions.length === 0 ? (
        <div
          style={{
            color: '#999',
            padding: '12px',
            background: '#fafafa',
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          尚未指派給任何個人
        </div>
      ) : (
        <List
          size='small'
          style={{ maxHeight: 240, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}
          dataSource={userPermissions}
          renderItem={perm => (
            <List.Item
              actions={[
                <Button
                  size='small'
                  danger
                  type='text'
                  icon={<DeleteOutlined />}
                  onClick={() => handleRevokeUser(perm.user_id, perm.user_name)}
                />,
              ]}
            >
              <List.Item.Meta
                avatar={<Avatar size='small' icon={<UserOutlined />} />}
                title={perm.user_name}
                description={<span style={{ fontSize: 12 }}>{perm.user_email}</span>}
              />
            </List.Item>
          )}
        />
      )}
      <div
        style={{ marginTop: 12, padding: 12, background: '#e6f4f8', borderRadius: 4, fontSize: 13 }}
      >
        個人授權會直接賦予該成員使用此規則集的權限，不受部門限制。
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <Space>
          <TeamOutlined />
          <span>規則指派 — {ruleSet?.rule_set_name}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={
        activeTab === 'department'
          ? [
              <Button key='cancel' onClick={onClose}>
                取消
              </Button>,
              <Button key='save' type='primary' loading={isSaving} onClick={handleSaveDepts}>
                儲存部門指派
              </Button>,
            ]
          : [
              <Button key='close' onClick={onClose}>
                關閉
              </Button>,
            ]
      }
      width={520}
      destroyOnClose
      afterClose={() => {
        setActiveTab('department');
        setSelectedUserId(null);
      }}
    >
      {ruleInfo}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'department',
            label: (
              <span>
                <TeamOutlined /> 部門分享
              </span>
            ),
            children: deptTab,
          },
          {
            key: 'user',
            label: (
              <span>
                <UserOutlined /> 個人指派
              </span>
            ),
            children: userTab,
          },
        ]}
      />
    </Modal>
  );
};

export default DepartmentAssignModal;
